import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useEduVerify } from "../lib/eduVerify";
import { useAuth } from "../lib/AuthContext";
import { EmSwash } from "../components/EmSwash";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * VerifyEdu — landing for /verify/edu?token=<t>.
 *
 * Public route (no auth required). The token itself is the proof — a
 * student got the email at the .edu they claimed, so possessing the
 * token means they own that inbox.
 */
export default function VerifyEdu() {
  useNoIndex();
  const location = useLocation();
  const { confirm } = useEduVerify({ auto: false });
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | ok | err
  const [payload, setPayload] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (!token) {
      setState("err");
      setErrMsg("This link is missing its verification token.");
      return;
    }
    confirm(token)
      .then((data) => {
        setPayload(data);
        setState("ok");
        refresh(); // pull fresh /auth/me so the badge lights up in nav
      })
      .catch((err) => {
        setState("err");
        setErrMsg(err?.response?.data?.detail || "That link couldn't be verified.");
      });
  }, [location.search, confirm, refresh]);

  return (
    <main data-testid="verify-edu-page" className="bg-sand">
      <Navbar
        onStart={() => {}}
        primaryLabel="Home"
        secondaryLabel={null}
      />
      <section className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-start justify-center px-6 py-24 lg:px-8">
        {state === "checking" && (
          <div className="flex items-center gap-3 text-olive" data-testid="verify-edu-checking">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="font-serif text-3xl">Confirming your .edu…</span>
          </div>
        )}

        {state === "ok" && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-2xl border-2 border-grass/40 bg-grass/8 p-8"
            data-testid="verify-edu-ok"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-grass/15 text-grass">
              <CheckCircle2 className="h-8 w-8" strokeWidth={2.2} />
            </span>
            <h1 className="mt-5 font-serif text-3xl lg:text-[3.5rem] leading-tight text-ink">
              Verified. <EmSwash>Welcome in.</EmSwash>
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-olive">
              We confirmed <strong className="text-ink">{payload?.edu_email}</strong>. Your future campaigns can go live once the platform launches.
            </p>
            <Link
              to="/dashboard"
              data-testid="verify-edu-back"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand hover:bg-forest"
            >
              Back to your dashboard
            </Link>
          </motion.div>
        )}

        {state === "err" && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full rounded-2xl border-2 border-scarlet/30 bg-scarlet/5 p-8"
            data-testid="verify-edu-err"
          >
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-scarlet/15 text-scarlet">
              <XCircle className="h-8 w-8" strokeWidth={2.2} />
            </span>
            <h1 className="mt-5 font-serif text-3xl lg:text-[3.5rem] leading-tight text-ink">
              This link couldn't be used.
            </h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-olive">
              {errMsg} You can request a new verification link from your dashboard.
            </p>
            <Link
              to="/dashboard"
              data-testid="verify-edu-back"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand hover:bg-forest"
            >
              Back to your dashboard
            </Link>
          </motion.div>
        )}
      </section>
      <Footer
        onStart={() => {}}
        onDonate={() => {}}
        primaryLabel="Home"
        secondaryLabel="Preview the platform"
      />
    </main>
  );
}
